import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  seoConfig,
  localBusinessSchema,
  serviceSchema,
} from '../../config/seoConfig';

const SEO = ({
  title: customTitle,
  description: customDescription,
  keywords: customKeywords,
  ogImage = 'https://gruppenbus.com/static/media/langer_bus_front.68901a1ffa3589a33375.webp',
  ogType = 'website',
}) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Hole SEO-Daten für die aktuelle Route oder verwende Standard-Startseiten-Daten
  const routeSEO = seoConfig[pathname] || seoConfig['/'];

  // Verwende custom props oder fall back auf Route-spezifische Daten
  const title = customTitle || routeSEO.title;
  const description = customDescription || routeSEO.description;
  const keywords = customKeywords || routeSEO.keywords;
  const canonicalUrl = routeSEO.canonicalUrl;

  // Safe JSON stringify für Tests
  const safeJSONStringify = (obj) => {
    try {
      return JSON.stringify(obj);
    } catch (error) {
      console.warn('JSON.stringify failed in SEO component:', error);
      return '{}';
    }
  };

  // Prüfe ob wir in einer Test-Umgebung sind
  const isTestEnvironment =
    process.env.NODE_ENV === 'test' ||
    typeof window === 'undefined' ||
    window.location?.hostname === 'localhost';

  return (
    <Helmet>
      {/* Basis Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="Kleinbus mieten Berlin - Gruppenbus"
      />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:locale" content="de_DE" />
      <meta
        property="og:site_name"
        content="Gruppenbus - Kleinbusvermietung Berlin"
      />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional Meta Tags */}
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <meta name="author" content="Gruppenbus Berlin" />
      <meta name="publisher" content="Gruppenbus Berlin" />
      <meta name="geo.region" content="DE-BE" />
      <meta name="geo.placename" content="Berlin" />
      <meta name="geo.position" content="52.5535644;13.4366988" />
      <meta name="ICBM" content="52.5535644, 13.4366988" />

      {/* Für mobile Optimierung */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Strukturierte Daten nur in Produktion und auf der Startseite */}
      {!isTestEnvironment && pathname === '/' && (
        <>
          <script type="application/ld+json">
            {safeJSONStringify(localBusinessSchema)}
          </script>
          <script type="application/ld+json">
            {safeJSONStringify(serviceSchema)}
          </script>
        </>
      )}

      {/* Route-spezifische strukturierte Daten für Unterseiten */}
      {!isTestEnvironment && pathname === '/busse' && (
        <script type="application/ld+json">
          {safeJSONStringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Kleinbus Vermietung Berlin',
            description:
              'Unsere verfügbaren Kleinbusse und 9-Sitzer für die Miete',
            itemListElement: [
              {
                '@type': 'Product',
                '@id': 'https://gruppenbus.com/busse#vw-crafter',
                name: 'VW Crafter Kleinbus',
                description:
                  '9-Sitzer Kleinbus mit Hochdach, ideal für Gruppenreisen',
                category: 'Vehicle',
                brand: {
                  '@type': 'Brand',
                  name: 'Volkswagen',
                },
                offers: {
                  '@type': 'Offer',
                  availability: 'https://schema.org/InStock',
                  priceCurrency: 'EUR',
                },
              },
              {
                '@type': 'Product',
                '@id': 'https://gruppenbus.com/busse#man-tge',
                name: 'MAN TGE Kleinbus',
                description:
                  'Moderner 9-Sitzer mit DVD-System und extra Komfort',
                category: 'Vehicle',
                brand: {
                  '@type': 'Brand',
                  name: 'MAN',
                },
                offers: {
                  '@type': 'Offer',
                  availability: 'https://schema.org/InStock',
                  priceCurrency: 'EUR',
                },
              },
            ],
          })}
        </script>
      )}

      {!isTestEnvironment && pathname === '/kontakt' && (
        <script type="application/ld+json">
          {safeJSONStringify({
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Kontakt - Gruppenbus Berlin',
            description:
              'Kontaktinformationen für die Kleinbusvermietung Gruppenbus in Berlin',
            mainEntity: {
              '@id': 'https://gruppenbus.com/#organization',
            },
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
